/**
 * Worcester Scale Co. - Inspection System
 *
 * Main application component with routing and layout.
 * Auth logic has been extracted to:
 * - services/authService.js - Pure auth operations
 * - contexts/AuthContext.js - React auth state management
 */

import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Box,
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material';
import { QueryProvider } from './providers/QueryProvider';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import { NotificationBell } from './components/shared';

// Critical components loaded immediately
import LoginScreen from './components/LoginScreen';

// Load auth test utilities in development only (side-effect imports)
if (process.env.NODE_ENV === 'development') {
  require('./utils/authTest');
  require('./utils/dbTest');
  require('./utils/systemDiagnostics');
  require('./utils/rlsDebugger');
}

// Lazy load all other components
const ManagerDashboard = lazy(() => import('./components/ManagerDashboardOptimized'));
const TechDashboard = lazy(() => import('./components/TechDashboard'));
const JobDetailScreen = lazy(() => import('./components/JobDetailScreen'));
const UnifiedInspectionWorkflow = lazy(() => import('./components/inspection/InspectionWorkflow'));
const CertificationForm = lazy(() => import('./components/CertificationForm'));
const ReportScreen = lazy(() => import('./components/ReportScreen'));
const CustomerPortal = lazy(() => import('./components/CustomerPortal'));
const CustomerSelection = lazy(() => import('./components/CustomerSelection'));
const UserManagementEnhanced = lazy(() => import('./components/UserManagementEnhanced'));
const UserOnboarding = lazy(() => import('./components/UserOnboarding'));
const ConsentPage = lazy(() => import('./components/ConsentPage'));
const StagingDataViewer = lazy(() => import('./components/StagingDataViewer'));
const InspectionReportView = lazy(() => import('./components/InspectionReportView'));
const StandardsManagement = lazy(() => import('./components/StandardsManagement'));
const ApprovalDashboard = lazy(() => import('./components/ApprovalDashboard'));
const AuditTrailViewer = lazy(() => import('./components/AuditTrailViewer'));
const CalibrationSchedule = lazy(() => import('./components/CalibrationSchedule'));
const ComplianceTracker = lazy(() => import('./components/ComplianceTracker'));
const NotFoundPage = lazy(() => import('./components/NotFoundPage'));

// Development-only components
const PdfPreviewPage = lazy(() => import('./components/dev/PdfPreviewPage'));
const DivisionInputTestPage = lazy(() => import('./components/dev/DivisionInputTestPage'));

/**
 * Loading fallback for lazy-loaded components
 */
function LoadingFallback() {
  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
      <CircularProgress />
    </Box>
  );
}

/**
 * App Header with navigation and user info
 */
function AppHeader() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  if (!user) return null;

  return (
    <AppBar position="static" className="no-print" sx={{ mb: 2, '@media print': { display: 'none !important' } }}>
      <Toolbar>
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          Worcester Scale Co., Inc. - Inspection System
        </Typography>
        <Typography variant="body1" sx={{ mr: 2 }}>
          Welcome, {user.full_name} ({user.role === 'admin' ? 'Admin' : user.role === 'manager' ? 'Manager' : 'Field Tech'})
        </Typography>
        {/* Notification Bell for managers and admins */}
        {(user.role === 'admin' || user.role === 'manager') && (
          <NotificationBell
            userId={user.id}
            onNotificationClick={(notification) => {
              if (notification.action_url) {
                navigate(notification.action_url);
              }
            }}
          />
        )}
        {(user.role === 'admin' || user.role === 'manager') && (
          <>
            <Button color="inherit" onClick={() => navigate('/manager')} sx={{ mr: 1 }}>
              Manager View
            </Button>
            <Button color="inherit" onClick={() => navigate('/tech')} sx={{ mr: 2 }}>
              Tech View
            </Button>
          </>
        )}
        <Button color="inherit" onClick={signOut}>
          Logout
        </Button>
      </Toolbar>
    </AppBar>
  );
}

/**
 * App Routes with authentication guards
 */
function AppRoutes() {
  const { user, loading } = useAuth();

  // CRITICAL: Don't render protected routes while auth is loading
  // This prevents redirect loops where user is briefly null
  if (loading) {
    return <LoadingFallback />;
  }

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        {/* Development-only routes - no auth required */}
        {process.env.NODE_ENV === 'development' && (
          <>
            <Route path="/dev/pdf-preview" element={<PdfPreviewPage />} />
            <Route path="/dev/division-input-test" element={<DivisionInputTestPage />} />
          </>
        )}

        <Route path="/login" element={<LoginScreen />} />

        <Route
          path="/manager"
          element={user?.role === 'admin' || user?.role === 'manager' ? <ManagerDashboard /> : <Navigate to="/login" />}
        />
        <Route
          path="/tech"
          element={user?.role === 'admin' || user?.role === 'manager' || user?.role === 'technician' ? <TechDashboard /> : <Navigate to="/login" />}
        />
        <Route
          path="/job/:jobId"
          element={user ? <JobDetailScreen /> : <Navigate to="/login" />}
        />
        <Route
          path="/inspection/:jobId"
          element={user ? <UnifiedInspectionWorkflow /> : <Navigate to="/login" />}
        />
        <Route
          path="/certification/:jobId"
          element={user ? <CertificationForm /> : <Navigate to="/login" />}
        />
        <Route
          path="/reports/:jobId"
          element={user ? <ReportScreen /> : <Navigate to="/login" />}
        />
        <Route
          path="/customers"
          element={user ? <CustomerPortal /> : <Navigate to="/login" />}
        />
        <Route
          path="/customer-selection"
          element={user?.role === 'manager' ? <CustomerSelection /> : <Navigate to="/login" />}
        />
        <Route
          path="/user-management"
          element={user?.role === 'admin' || user?.role === 'manager' ? <UserManagementEnhanced /> : <Navigate to="/login" />}
        />
        <Route
          path="/standards"
          element={user?.role === 'admin' || user?.role === 'manager' ? <StandardsManagement /> : <Navigate to="/login" />}
        />
        <Route
          path="/approvals"
          element={user?.role === 'admin' || user?.role === 'manager' ? <ApprovalDashboard /> : <Navigate to="/login" />}
        />
        <Route
          path="/audit-trail"
          element={user?.role === 'admin' ? <AuditTrailViewer /> : <Navigate to="/login" />}
        />
        <Route
          path="/calibration"
          element={user?.role === 'admin' || user?.role === 'manager' ? <CalibrationSchedule /> : <Navigate to="/login" />}
        />
        <Route
          path="/compliance"
          element={user?.role === 'admin' || user?.role === 'manager' ? <ComplianceTracker /> : <Navigate to="/login" />}
        />
        <Route path="/onboarding" element={<UserOnboarding />} />
        <Route path="/consent" element={<ConsentPage />} />
        <Route
          path="/staging-viewer"
          element={user ? <StagingDataViewer /> : <Navigate to="/login" />}
        />
        <Route
          path="/inspection-report/:inspectionId"
          element={user ? <InspectionReportView /> : <Navigate to="/login" />}
        />
        <Route
          path="/revision/:inspectionId"
          element={user ? <UnifiedInspectionWorkflow isRevisionMode={true} /> : <Navigate to="/login" />}
        />
        {/* Redirect stale notification URLs to valid routes */}
        <Route path="/inspections/:id" element={<Navigate to="/approvals" replace />} />
        <Route path="/approvals/:id" element={<Navigate to="/approvals" replace />} />
        <Route
          path="/"
          element={<Navigate to={user ? (user.role === 'manager' ? '/manager' : '/tech') : '/login'} />}
        />
        {/* Catch-all 404 - must be last */}
        <Route path="*" element={user ? <NotFoundPage /> : <Navigate to="/login" />} />
      </Routes>
    </Suspense>
  );
}

/**
 * Toast Snackbar component - supports success, warning, info, and error severities
 */
function ToastSnackbar() {
  const { error, clearError } = useAuth();

  return (
    <Snackbar
      open={!!error?.message}
      autoHideDuration={6000}
      onClose={clearError}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert onClose={clearError} severity={error?.severity || 'error'} sx={{ width: '100%' }}>
        {error?.message}
      </Alert>
    </Snackbar>
  );
}

/**
 * Main App Content (requires auth context)
 */
function AppContent() {
  const { loading } = useAuth();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography variant="h6">Loading Worcester Scale Co. System...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppHeader />
      <Container maxWidth="xl">
        <AppRoutes />
      </Container>
      <ToastSnackbar />
    </Box>
  );
}

/**
 * Main App Component
 * Sets up providers and error boundary
 */
function App() {
  return (
    <QueryProvider>
      <ErrorBoundary>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </ErrorBoundary>
    </QueryProvider>
  );
}

// Re-export useAuth for backward compatibility
export { useAuth } from './contexts/AuthContext';

export default App;
